import { format, parseISO } from "date-fns";

import { PROGRESS_OUTCOME_COUNT_BASIS } from "@/domains/progress/models/constants";
import type {
  ProgressChart,
  ProgressIntro,
  ProgressOutcomeItem,
  ProgressSymptom,
} from "@/domains/progress/models/types";
import { collectProgressIdealLines } from "@/domains/progress/utils/progress-page-data";

const RETURNING_PATIENT_TITLE = "Your first three months";

const NEW_PATIENT_TITLE = "Your next three months";

/**
 * The browser tab title, deliberately fixed and deliberately not the page heading: that
 * heading is personalized ("Your first three months, <FirstName>.") and a tab title is
 * copied into browser history and bookmarks, where a patient's name must never land.
 */
export const PROGRESS_DOCUMENT_TITLE = "Progress";

/** Fallback only: the server authors this paragraph as `intro.text` and its text wins. */
export const PROGRESS_RETURNING_PATIENT_INTRO =
  "Here is how your goals and symptoms have moved since you started.";

export const PROGRESS_NEW_PATIENT_INTRO =
  "This is where you are starting from. Everything you told us about is followed on its own, so you will see each one move at its own pace.";

export const PROGRESS_NEW_PATIENT_FIRST_POINT_EXPLAINER =
  "Your first check-in adds your first point.";

/**
 * "The same kind of symptom", because the cohort is keyed on symptom GROUPS and a group
 * rolls up several distinct symptoms: two patients can share the pain group while one
 * reported plain pain and the other joint pain. A cohort row pools every patient whose
 * groups merely intersect this patient's, so the guarantee is at least one shared group
 * and nothing stronger. The goal's own text plays no part in the cohort at all.
 */
export const PROGRESS_IDEAL_LINE_LEAD_SEVERITY_MATCHED =
  "The dashed line is where patients who report the same kind of symptom as you and started where you did typically get to.";

/** Says nothing about where the cohort started, because a widened one spans every band. */
export const PROGRESS_IDEAL_LINE_LEAD_WIDENED =
  "The dashed line is where patients who report the same kind of symptom as you typically get to.";

export const PROGRESS_CHARTS_SECTION_LABEL = "What you are following";

export const PROGRESS_OUTCOMES_TITLE = "Since you started";

export const PROGRESS_OUTCOMES_SUB_LINE = "From the health questions in your check-in.";

export const PROGRESS_SIDE_EFFECTS_TITLE = "Your Reported Side Effects";

export const PROGRESS_THANK_YOU_TITLE = "Thank you for showing up for your care plan.";

export const PROGRESS_POTENTIAL_DISCOUNT_LABEL = "Your EO member discount";

export const PROGRESS_BACK_TO_CAREPLAN_LABEL = "Back To My Care Plan";

export const PROGRESS_ERROR_TITLE = "We could not load your progress";

export const PROGRESS_ERROR_BODY =
  "Nothing you have sent us is lost. Please try again in a moment, and tell your care team if it keeps happening.";

export const PROGRESS_RETRY_LABEL = "Try Again";

export const PROGRESS_SIDE_EFFECT_NO_PRODUCT = "Not linked to a product";

export const PROGRESS_SIDE_EFFECT_NO_ACTION = "No action recorded.";

export const PROGRESS_NO_CHARTS_NOTE =
  "There is nothing to chart yet. As soon as your check-ins come in, everything you told us about will show up here.";

/** Blank counts as absent: the payload types these `z.string()`, so `??` alone lets "" through. */
export const orFallback = (value: string | null | undefined, fallback: string) => {
  const text = value?.trim() ?? "";

  return text.length > 0 ? text : fallback;
};

export const buildProgressPageTitle = ({
  firstName,
  isNewPatient,
}: {
  firstName: string;
  isNewPatient: boolean;
}) => {
  const heading = isNewPatient ? NEW_PATIENT_TITLE : RETURNING_PATIENT_TITLE;
  const name = firstName.trim();

  return name.length > 0 ? `${heading}, ${name}.` : `${heading}.`;
};

export const buildProgressPageIntro = ({
  intro,
  isNewPatient,
}: {
  intro?: ProgressIntro | null;
  isNewPatient: boolean;
}) => {
  const serverText = intro?.text.trim() ?? "";

  if (serverText.length > 0) {
    return serverText;
  }

  return isNewPatient ? PROGRESS_NEW_PATIENT_INTRO : PROGRESS_RETURNING_PATIENT_INTRO;
};

/**
 * Said once for the whole page, so it may only claim what holds for every line drawn on
 * it. One widened cohort drops the whole page to the weaker wording, because the reader
 * has no way to tell which line the sentence was written for.
 */
export const buildProgressIdealLineLead = (charts: ProgressChart[]) => {
  const idealLines = collectProgressIdealLines(charts);

  if (idealLines.length === 0) {
    return null;
  }

  const everyBandMatched = idealLines.every((idealLine) => {
    return idealLine.severityMatched;
  });

  return everyBandMatched
    ? PROGRESS_IDEAL_LINE_LEAD_SEVERITY_MATCHED
    : PROGRESS_IDEAL_LINE_LEAD_WIDENED;
};

/** Null when the date cannot be parsed: `format` throws a `RangeError` and takes the page down. */
export const formatProgressDate = (value: string) => {
  const parsed = parseISO(value);

  return Number.isNaN(parsed.getTime()) ? null : format(parsed, "MMMM d, yyyy");
};

export const buildOutcomeCountQualifier = (item: ProgressOutcomeItem) => {
  if (
    item.countBasis !== PROGRESS_OUTCOME_COUNT_BASIS.CHECK_INS_REPORTING ||
    item.checkInsAnswered <= 0
  ) {
    return null;
  }

  const checkIns = item.checkInsAnswered === 1 ? "check-in" : "check-ins";

  return `Reported in ${item.count} of ${item.checkInsAnswered} ${checkIns}.`;
};

/** "a", "a and b", "a, b and c" — the Oxford comma is deliberately absent. */
const formatSymptomList = (labels: string[]) => {
  if (labels.length <= 1) {
    return labels.join("");
  }

  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
};

/** Symptoms that map to no PROMIS group, so they can never produce a chart of their own. */
export const buildUnscoredSymptomsNote = (symptoms: ProgressSymptom[]) => {
  const labels = symptoms
    .map((symptom) => {
      return symptom.label.trim();
    })
    .filter((label) => {
      return label.length > 0;
    });

  if (labels.length === 0) {
    return null;
  }

  const tail =
    labels.length === 1
      ? "It does not have a chart of its own yet."
      : "They do not have charts of their own yet.";

  return `You also told us about ${formatSymptomList(labels)}. ${tail}`;
};
